import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { useModal } from '../../../contexts/ModalContext';

const PricingPhilosophy = () => {
  const { openConsultation } = useModal();
  const [activeTab, setActiveTab] = useState('philosophy');

  const philosophyPoints = [
    {
      icon: 'DollarSign',
      title: 'Transparent Pricing',
      description: 'No hidden fees, no surprise charges. Every cost is clearly outlined upfront.',
      detail: 'We believe in complete transparency. Our pricing structure is straightforward, with detailed breakdowns of every component. You\'ll know exactly what you\'re paying for and why.'
    },
    {
      icon: 'TrendingUp',
      title: 'Value-Driven Approach',
      description: 'Every dollar invested should deliver measurable business value and ROI.',
      detail: 'We focus on delivering solutions that drive real business results. Our pricing reflects the value we create, not just the time we spend. Your success is our success.'
    },
    {
      icon: 'Zap',
      title: 'Efficiency Through Innovation',
      description: 'Smart technology choices and streamlined processes reduce costs without compromising quality.',
      detail: 'By leveraging modern tools, automation, and proven frameworks, we can deliver premium results faster and more efficiently, passing those savings directly to you.'
    },
    {
      icon: 'Users',
      title: 'Scalable Solutions',
      description: 'Build once, scale forever. Our solutions grow with your business needs.',
      detail: 'We design architectures that can handle growth without requiring complete rebuilds. This long-term thinking saves you money and ensures your investment continues to pay dividends.'
    }
  ];

  const comparisonData = [
    {
      feature: 'Static Website',
      traditional: '₹50,000 - ₹2,00,000',
      bytepairs: '₹15,000 - ₹50,000',
      savings: '70%',
      highlight: 'Delivered in 1 day!'
    },
    {
      feature: 'Dynamic Web App',
      traditional: '₹2,00,000 - ₹10,00,000',
      bytepairs: '₹50,000 - ₹3,00,000',
      savings: '75%'
    },
    {
      feature: 'E-commerce Platform',
      traditional: '₹5,00,000 - ₹20,00,000',
      bytepairs: '₹1,50,000 - ₹6,00,000',
      savings: '70%'
    },
    {
      feature: 'Ongoing Maintenance',
      traditional: '₹50,000 - ₹2,00,000/month',
      bytepairs: '₹10,000 - ₹50,000/month',
      savings: '80%'
    },
    {
      feature: 'Time to Market',
      traditional: '6-12 months',
      bytepairs: '1 day - 3 months',
      savings: '90%',
      highlight: 'Lightning fast!'
    }
  ];

  const efficiencyFactors = [
    {
      icon: 'Layers',
      title: 'Modern Tech Stack',
      description: 'Using cutting-edge frameworks and tools that accelerate development.',
      impact: '40% faster development'
    },
    {
      icon: 'Repeat',
      title: 'Reusable Components',
      description: 'Building modular, reusable components that speed up future projects.',
      impact: '50% code reusability'
    },
    {
      icon: 'Bot',
      title: 'AI-Assisted Development',
      description: 'Leveraging AI tools for code generation and optimization.',
      impact: '30% productivity boost'
    },
    {
      icon: 'GitBranch',
      title: 'Streamlined Workflow',
      description: 'Optimized development processes and automated deployment pipelines.',
      impact: '60% faster deployment'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-muted/30 to-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-warning/10 text-warning px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="DollarSign" size={16} />
            <span>Pricing Philosophy</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Luxury Development at{' '}
            <span className="text-gradient bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              Startup Prices
            </span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Discover how we deliver enterprise-quality solutions at prices that make sense for growing businesses.
          </p>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap justify-center mb-12">
          <div className="bg-card border border-border rounded-xl p-2 inline-flex">
            {[
              { id: 'philosophy', label: 'Our Philosophy', icon: 'Heart' },
              { id: 'comparison', label: 'Cost Comparison', icon: 'BarChart3' },
              { id: 'efficiency', label: 'Efficiency Factors', icon: 'Zap' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg text-sm font-medium transition-all duration-300 ${activeTab === tab.id
                  ? 'bg-primary text-white shadow-brand'
                  : 'text-text-secondary hover:text-foreground hover:bg-muted'
                  }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Philosophy Tab */}
        {activeTab === 'philosophy' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {philosophyPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card border border-border rounded-2xl p-8 hover:shadow-brand-lg transition-all duration-300 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <Icon name={point.icon} size={24} color="white" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-4">{point.title}</h3>
                <p className="text-text-secondary mb-4 leading-relaxed">{point.description}</p>
                <p className="text-sm text-text-secondary leading-relaxed">{point.detail}</p>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Comparison Tab */}
        {activeTab === 'comparison' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-card border border-border rounded-2xl overflow-hidden shadow-lg"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-6 font-semibold text-foreground">Service</th>
                    <th className="text-left p-6 font-semibold text-text-secondary">Traditional Agency</th>
                    <th className="text-left p-6 font-semibold text-primary">Byte Pairs</th>
                    <th className="text-left p-6 font-semibold text-success">Your Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <motion.tr
                      key={row.feature}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="border-t border-border hover:bg-muted/30 transition-colors duration-300"
                    >
                      <td className="p-6 font-medium text-foreground">
                        <div className="flex items-center space-x-2">
                          <span>{row.feature}</span>
                          {row.highlight && (
                            <span className="inline-flex items-center space-x-1 bg-warning/10 text-warning px-2 py-1 rounded-full text-xs font-medium">
                              <Icon name="Zap" size={12} />
                              <span>{row.highlight}</span>
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-6 text-text-secondary">{row.traditional}</td>
                      <td className="p-6 text-primary font-semibold">{row.bytepairs}</td>
                      <td className="p-6">
                        <span className="inline-flex items-center space-x-1 bg-success/10 text-success px-3 py-1 rounded-full text-sm font-medium">
                          <Icon name="TrendingDown" size={14} />
                          <span>{row.savings}</span>
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Efficiency Tab */}
        {activeTab === 'efficiency' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {efficiencyFactors.map((factor, index) => (
              <motion.div
                key={factor.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-card border border-border rounded-2xl p-8 hover:shadow-brand-lg transition-all duration-300 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-accent to-success rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Icon name={factor.icon} size={20} color="white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-foreground mb-2">{factor.title}</h3>
                    <p className="text-text-secondary mb-4 leading-relaxed">{factor.description}</p>
                    <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                      <Icon name="TrendingUp" size={14} />
                      <span>{factor.impact}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              Ready to Experience the Difference?
            </h3>
            <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
              Get a free consultation and discover how we can deliver enterprise-quality solutions
              that fit your budget and timeline.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={openConsultation}
                className="inline-flex items-center space-x-2 bg-primary text-white px-8 py-4 rounded-xl font-medium hover:bg-primary/90 transition-colors duration-300 shadow-brand hover:shadow-brand-lg"
              >
                <Icon name="Calendar" size={20} />
                <span>Free Consultation</span>
              </button>
              <button
                onClick={openConsultation}
                className="inline-flex items-center space-x-2 bg-card border border-border text-foreground px-8 py-4 rounded-xl font-medium hover:bg-muted transition-colors duration-300"
              >
                <Icon name="Calculator" size={20} />
                <span>Get Quote</span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingPhilosophy;